import axios from 'axios';
import { tokenStorage } from './utils/tokenStorage';
import { getTenantHeaders, getTenantApiUrl } from './utils/enhancedTenantUtils';
import type { AxiosProgressEvent } from 'axios';

// Use tenant-aware API URL for proper routing
const API_BASE = getTenantApiUrl();

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true, // Enable sending cookies with requests
  timeout: 300000, // Increased to 300 seconds (5 minutes) for AI operations like SOP preview
  maxContentLength: Infinity,
  maxBodyLength: Infinity
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// ====================================================================
// NEW ADDITION: Helper function to get selected AI model from localStorage
// ====================================================================
const getSelectedAIModel = (): string => {
  try {
    const savedLLM = localStorage.getItem('selectedLLM');
    if (savedLLM) {
      const parsed = JSON.parse(savedLLM);
      return parsed.id || 'claude-4-sonnet';
    }
  } catch (error) {
    console.error('Error getting selected AI model:', error);
  }
  return 'claude-4-sonnet'; // Default fallback
};

// ====================================================================
// EXISTING REQUEST INTERCEPTOR - ENHANCED WITH AI MODEL HEADER
// ====================================================================
api.interceptors.request.use((config) => {
  // Add tenant headers for proper tenant isolation
  const tenantHeaders = getTenantHeaders();
  Object.assign(config.headers, tenantHeaders);
  
  // With httpOnly cookies, we don't need to manually add the token
  // The browser will automatically send the cookies
  // But we'll keep the Authorization header as fallback for backward compatibility
  const token = tokenStorage.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // Ensure credentials are included
  config.withCredentials = true;
  
  // NEW ADDITION: Add selected AI model to headers
  const selectedModel = getSelectedAIModel();
  config.headers['X-AI-Model'] = selectedModel;
  
  return config;
});

// ====================================================================
// EXISTING RESPONSE INTERCEPTOR - UNCHANGED
// ====================================================================
api.interceptors.response.use(
  (response) => {
    // Check for token expiry warning
    const tokenWarning = response.headers['x-token-expiry-warning'];
    const expiresIn = response.headers['x-token-expires-in'];

    if (tokenWarning === 'true' && expiresIn) {
      // Token is about to expire, refresh proactively
      const timeLeft = parseInt(expiresIn);
      if (timeLeft < 900) { // Less than 15 minutes (changed from 5 minutes)
        refreshToken();
      }
    } else if (tokenStorage.isAccessTokenExpiring()) {
      // Check our local expiry tracking as well
      refreshToken();
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle IP whitelist block - redirect to login with message
    if (error.response?.status === 403 && error.response?.data?.code === 'IP_NOT_WHITELISTED') {
      // Clear tokens and redirect to login
      tokenStorage.clearTokens();
      delete api.defaults.headers.common['Authorization'];

      // Store error message to show on login page
      sessionStorage.setItem('ip_blocked_message', 'Access denied: Your IP address is not authorized to access this application.');

      // Redirect to login
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && 
        error.response?.data?.code === 'TOKEN_EXPIRED' && 
        !originalRequest._retry) {
      
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshTokenValue = tokenStorage.getRefreshToken();
        if (!refreshTokenValue) {
          throw new Error('No refresh token');
        }

        const response = await axios.post(`${API_BASE}/api/auth/refresh`, {
          refreshToken: refreshTokenValue
        }, {
          withCredentials: true // Ensure cookies are sent
        });

        // With httpOnly cookies, tokens are not returned in response body
        // They're automatically set as cookies by the browser
        // We'll check if tokens are still returned for backward compatibility
        const { accessToken, refreshToken: newRefreshToken } = response.data;
        
        if (accessToken && newRefreshToken) {
          // Backward compatibility: if tokens are returned, store them
          tokenStorage.setTokens(accessToken, newRefreshToken);
        }
        
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        
        processQueue(null, accessToken);
        
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        
        // Refresh failed, logout user
        tokenStorage.clearTokens();
        delete api.defaults.headers.common['Authorization'];
        
        // Dispatch logout event
        window.dispatchEvent(new CustomEvent('auth:logout'));
        
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ====================================================================
// EXISTING REFRESH TOKEN FUNCTION - UNCHANGED
// ====================================================================
const refreshToken = async () => {
  try {
    const refreshTokenValue = tokenStorage.getRefreshToken();
    if (!refreshTokenValue) return;

    const response = await axios.post(`${API_BASE}/api/auth/refresh`, {
      refreshToken: refreshTokenValue
    });

    const { accessToken, refreshToken: newRefreshToken } = response.data;
    
    tokenStorage.setTokens(accessToken, newRefreshToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
  } catch (error) {
    console.error('Proactive token refresh failed:', error);
    // If proactive refresh fails, clear tokens to force re-login
    tokenStorage.clearTokens();
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }
};

// ====================================================================
// EXISTING UPLOAD FUNCTION - UNCHANGED
// ====================================================================
export const uploadDocuments = (formData: FormData, onUploadProgress?: (progressEvent: AxiosProgressEvent) => void) => {
  return api.post('/api/documents/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 300000, // 5 minutes
    onUploadProgress,
  });
};

// ====================================================================
// EXISTING API CLIENT EXPORT - UNCHANGED
// ====================================================================
export const apiClient = api;

// ====================================================================
// DUAL-MODE DOCUMENT PROCESSING API FUNCTIONS
// ====================================================================

// Document Assessment Interfaces
export interface DocumentAssessment {
  format_type: 'swimlane_process_map' | 'narrative_sop' | 'hybrid' | 'unstructured';
  structure_quality: {
    score: number;
    has_clear_sections: boolean;
    has_numbered_steps: boolean;
    has_stakeholder_definitions: boolean;
    has_decision_logic: boolean;
    has_consistent_formatting: boolean;
    has_controls: boolean;
  };
  completeness: {
    score: number;
    identified_gaps: Array<{
      gap_id: string;
      gap_type: string;
      description: string;
      priority: 'high' | 'medium' | 'low';
      suggested_fill?: string;
    }>;
  };
  recommendation: 'preserve' | 'interpret' | 'hybrid';
  recommendation_reason: string;
}

export interface GapSuggestion {
  source_type: 'interview' | 'change_detection' | 'ai_inference' | 'manual';
  source_id: number | null;
  source_attribution: string;
  suggested_text: string;
  relevance_score: number;
  confidence_score: number;
}

export interface DocumentGap {
  gap_id: string;
  gap_type: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  suggested_fill?: string;
  suggestions: GapSuggestion[];
  has_auto_suggestions: boolean;
}

export interface DocumentEnhancement {
  id: number;
  document_id: number;
  enhancement_type: 'addition' | 'gap_fill' | 'inference' | 'clarification';
  location_reference?: string;
  original_context?: string;
  enhancement_text: string;
  source_type: 'interview' | 'change_detection' | 'ai_inference' | 'manual';
  source_id?: number;
  source_attribution?: string;
  confidence_score?: number;
  is_accepted: boolean;
  is_visible: boolean;
  gap_id?: string;
  created_at: string;
}

// Assess document structure
export const assessDocument = async (documentId: number, modelId?: string) => {
  const response = await api.post(`/api/documents/${documentId}/assess`, { modelId });
  return response.data;
};

// Get document assessment
export const getDocumentAssessment = async (documentId: number) => {
  const response = await api.get(`/api/documents/${documentId}/assessment`);
  return response.data;
};

// Set processing mode for document
export const setDocumentProcessingMode = async (documentId: number, mode: 'preserve' | 'interpret') => {
  console.log('🔥🔥🔥 [FRONTEND API] setDocumentProcessingMode called!');
  console.log(`   - Document ID: ${documentId}`);
  console.log(`   - Mode: ${mode}`);
  console.log(`   - API URL: /api/documents/${documentId}/processing-mode`);

  const response = await api.post(`/api/documents/${documentId}/processing-mode`, { mode });
  console.log('✅ [FRONTEND API] API call successful:', response.data);
  return response.data;
};

// Get gap suggestions for a document
export const getDocumentGaps = async (documentId: number): Promise<{
  document_id: number;
  total_gaps: number;
  gaps: DocumentGap[];
}> => {
  const response = await api.get(`/api/documents/${documentId}/gaps`);
  return response.data;
};

// Apply a gap fill
export const applyGapFill = async (
  documentId: number,
  gapId: string,
  data: {
    fillText: string;
    sourceType?: string;
    sourceId?: number;
    sourceAttribution?: string;
    confidenceScore?: number;
    locationReference?: string;
  }
) => {
  const response = await api.post(`/api/documents/${documentId}/gaps/${gapId}/fill`, data);
  return response.data;
};

// Add manual enhancement
export const addDocumentEnhancement = async (
  documentId: number,
  data: {
    enhancementText: string;
    locationReference?: string;
    originalContext?: string;
  }
) => {
  const response = await api.post(`/api/documents/${documentId}/enhancements`, data);
  return response.data;
};

// Get all enhancements for a document
export const getDocumentEnhancements = async (documentId: number): Promise<{
  document_id: number;
  processing_mode: string;
  original_content_preserved: boolean;
  total_enhancements: number;
  enhancements: DocumentEnhancement[];
}> => {
  const response = await api.get(`/api/documents/${documentId}/enhancements`);
  return response.data;
};

// Delete an enhancement
export const deleteDocumentEnhancement = async (documentId: number, enhancementId: number) => {
  const response = await api.delete(`/api/documents/${documentId}/enhancements/${enhancementId}`);
  return response.data;
};

// Batch assess multiple documents
export const batchAssessDocuments = async (documentIds: number[], modelId?: string) => {
  const response = await api.post('/api/documents/batch-assess', { documentIds, modelId });
  return response.data;
};

// ====================================================================
// NEW ADDITIONS: AI MODEL MANAGEMENT INTERFACES AND FUNCTIONS
// ====================================================================

// NEW: AI Model Interface
export interface AIModel {
  id: string;
  name: string;
  provider: string;
  available: boolean;
  features: string[];
}

// NEW: Enhanced request interfaces with AI model selection
export interface DocumentAnalysisRequest {
  projectId: string;
  selectedAIModel?: string;
  useEnhanced?: boolean;
}

export interface DocumentAnalysisResponse {
  document_id: string;
  analysis_id: string;
  confidence_score: number;
  ai_model_used: string;
  provider: string;
  processing_method: string;
  processes_found: number;
  controls_found: number;
  risks_identified: number;
  success: boolean;
  message: string;
  enhanced_service_used?: boolean; 
}

export interface AskAIStepRequest {
  projectId: string;
  step: string;
  question: string;
  stepIndex?: number;
  options?: {
    useWebSearch?: boolean;
    enableStepExpansion?: boolean;
    provideCitationOnly?: boolean;
    createChecklist?: boolean;
  };
  selectedAIModel?: string;
}

export interface AskAIStepResponse {
  answer: string;
  sources: Array<{
    document_name: string;
    relevance_score: number;
  }>;
  expanded_steps?: string[];
  checklist?: string[];
  steps_updated: boolean;
  ai_model_used: string;
  provider: string;
}

// NEW: AI Model Management Functions
export const getAvailableAIModels = async (): Promise<{ models: AIModel[]; default: string }> => {
  const response = await api.get('/api/analysis/ai-models');
  return response.data;
};

// NEW: Set AI model selection
export const setAIModel = (modelId: string, modelName: string): void => {
  localStorage.setItem('selectedLLM', JSON.stringify({ 
    id: modelId, 
    name: modelName 
  }));
  
  // Update default header for future requests
  api.defaults.headers['X-AI-Model'] = modelId;
};

// NEW: Get current AI model selection
export const getCurrentAIModel = (): { id: string; name: string } => {
  try {
    const savedLLM = localStorage.getItem('selectedLLM');
    if (savedLLM) {
      return JSON.parse(savedLLM);
    }
  } catch (error) {
    console.error('Error getting current AI model:', error);
  }
  return { id: 'claude-4-sonnet', name: 'Claude 4 Sonnet' };
};

// ====================================================================
// NEW ADDITIONS: ENHANCED API FUNCTIONS WITH AI MODEL SUPPORT
// ====================================================================

// NEW: Enhanced document analysis with AI model selection
export const analyzeDocumentWithAI = async (
  documentId: string, 
  data: DocumentAnalysisRequest
): Promise<DocumentAnalysisResponse> => {
  const payload = {
    ...data,
    selectedAIModel: data.selectedAIModel || getSelectedAIModel()
  };
  
  const response = await api.post(`/api/documents/${documentId}/analyze`, payload);
  return response.data;
};

// NEW: Enhanced bulk analysis with AI model selection
export const bulkAnalyzeDocuments = async (data: {
  projectId: string;
  documentIds?: string[];
  selectedAIModel?: string;
}) => {
  const payload = {
    ...data,
    selectedAIModel: data.selectedAIModel || getSelectedAIModel()
  };
  
  const response = await api.post('/api/documents/bulk-analyze', payload);
  return response.data;
};

// NEW: Enhanced AI step analysis
export const askAIAboutStep = async (data: AskAIStepRequest): Promise<AskAIStepResponse> => {
  const payload = {
    ...data,
    selectedAIModel: data.selectedAIModel || getSelectedAIModel()
  };
  
  const response = await api.post('/api/analysis/ask-ai-step', payload);
  return response.data;
};

// NEW: Enhanced step expansion
export const expandStep = async (data: {
  projectId: string;
  step: string;
  stepIndex: number;
  context?: string[];
  selectedAIModel?: string;
}) => {
  const { projectId, ...payload } = data;
  const payloadWithModel = {
    ...payload,
    selectedAIModel: data.selectedAIModel || getSelectedAIModel()
  };
  
  const response = await api.post(`/api/analysis/expand-step/${projectId}`, payloadWithModel);
  return response.data;
};

// NEW: Enhanced general AI query
export const askAI = async (data: {
  projectId: string;
  question: string;
  context?: string;
  selectedAIModel?: string;
}) => {
  const payload = {
    ...data,
    selectedAIModel: data.selectedAIModel || getSelectedAIModel()
  };
  
  const response = await api.post('/api/analysis/ask-ai', payload);
  return response.data;
};

// NEW: Enhanced SOP generation with AI model selection
export const generateSOPWithAI = async (
  projectId: string,
  type: 'basic' | 'enhanced' = 'basic',
  selectedAIModel?: string
) => {
  const payload = {
    selectedAIModel: selectedAIModel || getSelectedAIModel()
  };
  
  const endpoint = type === 'enhanced' 
    ? `/api/analysis/generate-enhanced-sop/${projectId}`
    : `/api/analysis/generate-basic-sop/${projectId}`;
  
  const response = await api.post(endpoint, payload);
  return response.data;
};

// NEW: Enhanced process map generation with AI model selection
export const generateProcessMapWithAI = async (
  projectId: string,
  level: number = 2,
  selectedAIModel?: string
) => {
  const payload = {
    level,
    selectedAIModel: selectedAIModel || getSelectedAIModel()
  };
  
  const response = await api.post(`/api/analysis/generate-process-map/${projectId}`, payload);
  return response.data;
};

// NEW: Enhanced analysis start with AI model selection
export const startAnalysisWithAI = async (
  projectId: string,
  selectedAIModel?: string
) => {
  const payload = {
    selectedAIModel: selectedAIModel || getSelectedAIModel()
  };
  
  const response = await api.post(`/api/analysis/start/${projectId}`, payload);
  return response.data;
};

// interview-response/:projectId
export const interviewResponse = async (
  projectId: string,
  selectedAIModel?: string
) => {
  const payload = {
    selectedAIModel: selectedAIModel || getSelectedAIModel()
  };
  
  const response = await api.post(`/api/analysis/interview-response/${projectId}`, payload);
  return response.data;
};

// Process Maps with Audit Trail API Endpoints
export const getProcessMapsForProject = async (projectId: string) => {
  const response = await api.get(`/api/analysis/projects/${projectId}/process-maps`);
  return response.data;
};

export const getProcessMapAuditTrail = async (mapId: string) => {
  const response = await api.get(`/api/analysis/process-maps/${mapId}/audit-trail`);
  return response.data;
};

export const exportProcessMapAuditPDF = async (mapId: string, includeTechnicalDetails: boolean = true) => {
  const response = await api.get(`/api/analysis/process-maps/${mapId}/audit-trail/pdf`, {
    params: { includeTechnicalDetails },
    responseType: 'blob'
  });
  return response.data;
};

export const exportProcessMapAuditCSV = async (mapId: string) => {
  const response = await api.get(`/api/analysis/process-maps/${mapId}/audit-trail/csv`, {
    responseType: 'blob'
  });
  return response.data;
};

export const updateProcessMapWithEdits = async (
  mapId: string, 
  stakeholderEdits: any, 
  userNotes?: string
) => {
  const response = await api.put(`/api/analysis/process-maps/${mapId}`, {
    stakeholder_edits: stakeholderEdits,
    user_notes: userNotes
  });
  return response.data;
};

// Enhanced process map generation with better error handling
export const generateProcessMapEnhanced = async (
  projectId: string, 
  level: number, 
  selectedAIModel?: string
) => {
  const payload = {
    level,
    selectedAIModel: selectedAIModel || getSelectedAIModel()
  };
  
  const response = await api.post(`/api/analysis/projects/${projectId}/process-maps`, payload);
  return response.data;
};

// ====================================================================
// TENANT MANAGEMENT API FUNCTIONS
// ====================================================================

// Tenant interfaces
export interface TenantData {
  id: number;
  subdomain: string;
  name: string;
  domain?: string;
  status: 'active' | 'inactive' | 'trial' | 'suspended';
  subscription_tier: 'basic' | 'professional' | 'enterprise';
  contact_email: string;
  billing_email?: string;
  max_users: number;
  max_projects: number;
  storage_limit_gb: number;
  api_rate_limit: number;
  trial_ends_at?: string;
  subscription_started_at?: string;
  created_at: string;
  updated_at: string;
  created_by_user_id?: number;
  settings?: Record<string, any>;
  branding?: Record<string, any>;
  usage?: {
    user_count: number;
    project_count: number;
    storage_used_gb: number;
  };
}

export interface CreateTenantRequest {
  subdomain: string;
  name: string;
  domain?: string;
  subscription_tier: 'basic' | 'professional' | 'enterprise';
  contact_email: string;
  billing_email?: string;
  trial_ends_at?: string;
  settings?: Record<string, any>;
  branding?: Record<string, any>;
  max_users?: number;
  max_projects?: number;
  storage_limit_gb?: number;
  api_rate_limit?: number;
}

export interface UpdateTenantRequest {
  name?: string;
  domain?: string;
  status?: 'active' | 'inactive' | 'trial' | 'suspended';
  subscription_tier?: 'basic' | 'professional' | 'enterprise';
  max_users?: number;
  max_projects?: number;
  storage_limit_gb?: number;
  contact_email?: string;
  billing_email?: string;
  trial_ends_at?: string;
  settings?: Record<string, any>;
  branding?: Record<string, any>;
}

export interface TenantsResponse {
  tenants: TenantData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface PlanConfig {
  max_users: number;
  max_projects: number;
  storage_limit_gb: number;
  api_rate_limit: number;
  trial_days: number;
  features: Record<string, boolean>;
}

export interface PlansResponse {
  plans: Record<string, PlanConfig>;
  available_tiers: string[];
}

// Get all tenants (SUPER_ADMIN only)
export const getTenants = async (params?: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}): Promise<TenantsResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.status) queryParams.append('status', params.status);
  if (params?.search) queryParams.append('search', params.search);

  const response = await api.get(`/api/tenants?${queryParams}`);
  return response.data;
};

// Get current tenant information
export const getCurrentTenant = async (): Promise<{ tenant: TenantData }> => {
  const response = await api.get('/api/tenants/current');
  return response.data;
};

// Get full current tenant information (authenticated users)
export const getCurrentTenantFull = async (): Promise<{ tenant: TenantData }> => {
  const response = await api.get('/api/tenants/current/full');
  return response.data;
};

// Get specific tenant by ID (SUPER_ADMIN only)
export const getTenant = async (tenantId: number): Promise<{ tenant: TenantData }> => {
  const response = await api.get(`/api/tenants/${tenantId}`);
  return response.data;
};

// Create new tenant (SUPER_ADMIN only)
export const createTenant = async (tenantData: CreateTenantRequest): Promise<{ tenant: TenantData }> => {
  const response = await api.post('/api/tenants', tenantData);
  return response.data;
};

// Update tenant (SUPER_ADMIN only)
export const updateTenant = async (
  tenantId: number, 
  tenantData: UpdateTenantRequest
): Promise<{ tenant: TenantData }> => {
  const response = await api.put(`/api/tenants/${tenantId}`, tenantData);
  return response.data;
};

// Update current tenant (Admin only)
export const updateCurrentTenant = async (
  tenantData: Partial<Pick<UpdateTenantRequest, 'name' | 'contact_email' | 'billing_email' | 'settings' | 'branding'>>
): Promise<{ tenant: TenantData }> => {
  const response = await api.put('/api/tenants/current', tenantData);
  return response.data;
};

// Activate tenant (SUPER_ADMIN only)
export const activateTenant = async (tenantId: number): Promise<{ message: string; tenant: TenantData }> => {
  const response = await api.post(`/api/tenants/${tenantId}/activate`);
  return response.data;
};

// Deactivate tenant (SUPER_ADMIN only)
export const deactivateTenant = async (tenantId: number): Promise<{ message: string }> => {
  const response = await api.delete(`/api/tenants/${tenantId}`);
  return response.data;
};

// Get tenant usage statistics (SUPER_ADMIN only)
export const getTenantStats = async (tenantId: number): Promise<{ stats: any }> => {
  const response = await api.get(`/api/tenants/${tenantId}/stats`);
  return response.data;
};

// Check subdomain availability (SUPER_ADMIN only)
export const checkSubdomainAvailability = async (subdomain: string): Promise<{ available: boolean; subdomain: string }> => {
  const response = await api.get(`/api/tenants/check-subdomain/${subdomain}`);
  return response.data;
};

// Get available subscription plans
export const getTenantPlans = async (): Promise<PlansResponse> => {
  const response = await api.get('/api/tenants/plans');
  return response.data;
};

// ====================================================================
// ENHANCED TENANT MANAGEMENT (SUPER_ADMIN ONLY)
// ====================================================================

export interface EnhancedTenantData extends TenantData {
  analytics: {
    totalUsers: number;
    activeUsers: number;
    totalProjects: number;
    totalDocuments: number;
    storageUsed: number;
    storageLimit: number;
    apiCalls: number;
    subscriptionInfo: {
      plan: string;
      status: string;
      nextBilling: string;
      amount: number;
      currency: string;
    };
    monthlyActivity: Array<{
      date: string;
      users: number;
      projects: number;
      documents: number;
    }>;
    userGrowth: Array<{
      date: string;
      count: number;
    }>;
  };
  users?: Array<{
    id: number;
    name: string;
    email: string;
    role: string;
    last_login: string;
    created_at: string;
    is_active: boolean;
    projectCount?: number;
    documentCount?: number;
  }>;
}

export interface EnhancedTenantsResponse {
  success: boolean;
  tenants: EnhancedTenantData[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Get enhanced tenant list with analytics (SUPER_ADMIN only)
export const getEnhancedTenants = async (params?: {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}): Promise<EnhancedTenantsResponse> => {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.status) queryParams.append('status', params.status);
  if (params?.search) queryParams.append('search', params.search);

  const response = await api.get(`/api/super-admin/tenants/enhanced?${queryParams}`);
  return response.data;
};

// Get detailed tenant information with full analytics (SUPER_ADMIN only)
export const getTenantDetails = async (tenantId: number): Promise<{
  success: boolean;
  tenant: EnhancedTenantData;
}> => {
  const response = await api.get(`/api/super-admin/tenants/${tenantId}/details`);
  return response.data;
};

// Update tenant status (SUPER_ADMIN only)
export const updateTenantStatus = async (
  tenantId: number, 
  status: string
): Promise<{ success: boolean; message: string; tenant: TenantData }> => {
  const response = await api.patch(`/api/super-admin/tenants/${tenantId}/status`, { status });
  return response.data;
};

// Update user role within a tenant (SUPER_ADMIN only)
export const updateTenantUserRole = async (
  tenantId: number,
  userId: number,
  role: string
): Promise<{ success: boolean; message: string; user: any }> => {
  const response = await api.patch(`/api/super-admin/tenants/${tenantId}/users/${userId}/role`, { role });
  return response.data;
};

// Delete user from a tenant (SUPER_ADMIN only)
export const deleteTenantUser = async (
  tenantId: number,
  userId: number
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`/api/super-admin/tenants/${tenantId}/users/${userId}`);
  return response.data;
};

// Invite user to a tenant (SUPER_ADMIN only)
export const inviteTenantUser = async (
  tenantId: number,
  userData: {
    email: string;
    name: string;
    role?: string;
  }
): Promise<{ 
  success: boolean; 
  message: string; 
  user: any; 
  temporaryPassword: string;
}> => {
  const response = await api.post(`/api/super-admin/tenants/${tenantId}/users/invite`, userData);
  return response.data;
};

// Export tenant analytics data (SUPER_ADMIN only)
export const exportTenantAnalytics = async (
  tenantId: number,
  format: 'csv' | 'json' = 'csv'
): Promise<any> => {
  const response = await api.get(`/api/super-admin/tenants/${tenantId}/analytics/export?format=${format}`, {
    responseType: format === 'csv' ? 'blob' : 'json'
  });
  return response.data;
};

// ====================================================================
// NEW ADDITION: Process Map Incremental Update & SOP Preview APIs
// ====================================================================

// Process Map Change interfaces
export interface ProcessMapChange {
  type: 'add_step' | 'modify_step' | 'delete_step';
  step_id?: string;
  data?: {
    id?: string;
    name?: string;
    step_title?: string;
    description?: string;
    step_number?: string;
    validation_status?: string;
  };
  new_step?: {
    id?: string;
    title: string;
    description?: string;
    category?: string;
    step_number?: string;
  };
  insert_after?: string;
  field?: string;
  new_value?: string;
}

export interface IncrementalUpdateRequest {
  changes: ProcessMapChange[];
  preserve_validated?: boolean;
  auto_sync_sop?: boolean;
}

export interface IncrementalUpdateResponse {
  success: boolean;
  message: string;
  process_map: {
    id: number;
    process_name: string;
    total_steps: number;
  };
  changes_applied: number;
  changes_failed: number;
  changes_total: number;
  change_details: Array<{
    type: string;
    step_id?: string;
    success: boolean;
    message: string;
  }>;
  sop_sync?: any;
  preserve_validated: boolean;
  auto_sync_sop: boolean;
}

export interface SOPPreviewResponse {
  success: boolean;
  message: string;
  preview: {
    title: string;
    sections: Array<{
      section_number: string;
      section_title: string;
      content: string;
      subsections: any[];
      preserved: boolean;
      validation_status: string;
      metadata?: {
        source_type: string;
        validation_status: string;
        ai_confidence?: number;
      };
    }>;
    metadata: {
      process_map_id: number;
      process_name: string;
      level: number;
      total_steps: number;
      generated_from: string;
      generated_at: string;
      preserve_verified: boolean;
      is_preview: boolean;
    };
    summary: {
      total_sections: number;
      process_map_level: number;
      preserved_sections: number;
      new_sections: number;
      generated_using: string;
    };
    version: string;
    generated_at: string;
    generated_from: string;
  };
  diff_analysis?: {
    comparison_with_existing: {
      existing_sections_count: number;
      new_sections_count: number;
      changes: {
        added_sections: Array<{ title: string }>;
        modified_sections: Array<{ title: string }>;
        removed_sections: Array<{ section_number: string; title: string }>;
      };
    };
  };
  process_map: {
    id: number;
    process_name: string;
    level: number;
    total_steps: number;
  };
}

// Apply incremental updates to process map
export const applyIncrementalUpdate = async (
  processMapId: string,
  request: IncrementalUpdateRequest
): Promise<IncrementalUpdateResponse> => {
  const response = await api.post(`/api/analysis/process-maps/${processMapId}/incremental-update`, request);
  return response.data;
};

// Get SOP preview for process map
export const getSOPPreview = async (
  processMapId: string,
  options?: {
    preserve_verified?: boolean;
    include_diff?: boolean;
    include_metadata?: boolean;
    preview_mode?: string;
  }
): Promise<SOPPreviewResponse> => {
  const queryParams = new URLSearchParams();
  if (options?.preserve_verified !== undefined) {
    queryParams.append('preserve_verified', options.preserve_verified.toString());
  }
  if (options?.include_diff !== undefined) {
    queryParams.append('include_diff', options.include_diff.toString());
  }
  if (options?.include_metadata !== undefined) {
    queryParams.append('include_metadata', options.include_metadata.toString());
  }
  if (options?.preview_mode) {
    queryParams.append('preview_mode', options.preview_mode);
  }

  const response = await api.get(`/api/analysis/process-maps/${processMapId}/sop-preview?${queryParams}`, {
    timeout: 300000 // 5 minutes timeout for AI generation
  });
  return response.data;
};

export default api;